import React, { createContext, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { debounce } from 'lodash';

import { findAncestor } from 'utils/dom_utils';
import Button from 'components/shared/Button';

import { useAppDispatch, useAppSelector } from 'utils/hooks';

import activeEditor from 'domains/active_editor';

import canvases from 'domains/canvases';
import products from 'domains/products';
import ui from 'domains/ui';

import { paramFilterDetail } from 'utils/globals';
import { notNull } from 'utils/type_guards';

import ShufflerProduct, { propInShufflerProduct } from './ShufflerProduct';
import ShufflerProductTile from './ShufflerProductTile';
import ShufflerFilterGroup from './ShufflerFilterGroup';
import { StandardControls, MobileControls } from './Controls';
import ShufflerProductDetail from './ShufflerProductDetail';
import ShufflerWalkthrough from './Walkthrough';

import FilterData from './data/filters';

import type { ShufflerFilterData, ShufflerFilterItem } from './data/filters.types';
import type { OptionGroupAPIData } from 'domains/option_groups';
import type { OptionalPair } from 'types/common_types';

export type ShufflerContextType = {
  itemClick: (e: React.SyntheticEvent) => void;
  handleItemNavigation: (e: React.KeyboardEvent) => void;
  byFilter: (target: EventTarget) => void;
  metalabels: Record<string, string>;
  selectedFilters: Set<string>;
  setSelectedFilters: (selected: Set<string>) => void;
  filterCollapse: React.MouseEventHandler;
  collapseFilters: Set<string>;
  showWalkthrough: boolean;
  setShowWalkthrough: (set: boolean) => void;
  filterSearch: (target: HTMLInputElement) => void;
  catalogOptionGroups: string[];
  handleZoom: (z: number) => void;
  numIcons: number;
  sortBy: string;
  setSortBy: (sort: string) => void;
  staticFilterData: ShufflerFilterData | null;
  productOptionGroups: OptionGroupAPIData[];
  relatedProducts: ShufflerProduct[];
  showRelatedProducts: boolean;
  setShowRelatedProducts: (show: boolean) => void;
  product: ShufflerProduct | undefined;
  setProduct: (p: ShufflerProduct | undefined) => void;
  currentOptionGroup: OptionGroupAPIData | undefined;
  setCurrentOptionGroup: (optionGroup: OptionGroupAPIData | undefined) => void;
  selectedOptions: Map<string, number>;
  optionClick: (e: React.MouseEvent<HTMLDivElement> | React.KeyboardEvent<HTMLDivElement>) => void;
  optionGroupClick: (
    e: React.MouseEvent<HTMLDivElement> | React.KeyboardEvent<HTMLDivElement>,
    force?: boolean
  ) => void;
};

export const ShufflerContext = createContext<ShufflerContextType | null>(null);

const metalabels = { numphotos: 'Number of photos', hascustomtext: 'Custom text' };

function Shuffler() {
  const dispatch = useAppDispatch();
  const activeCatalog = useAppSelector(activeEditor.selectors.catalog);
  const subcatalogs = useAppSelector(activeEditor.selectors.subcatalogs);
  const activeProduct = useAppSelector(activeEditor.selectors.product);
  const optionGroups = useAppSelector(activeEditor.selectors.optionGroups);
  const productsById = useAppSelector(products.selectors.selectEntities);
  const hasMobileNavigation = useAppSelector(ui.selectors.hasMobileNavigation);
  const showAdvancedFilters = useAppSelector(ui.selectors.showAdvancedFilters);

  const elem = useRef<HTMLDivElement>(null);
  const filtersElem = useRef<HTMLDivElement>(null);
  const mobileFooterElem = useRef<HTMLDivElement>(null);
  const shufflerElem = useRef<HTMLDivElement>(null);

  const [init, setInit] = useState(false);
  const [status, setStatus] = useState('pending');
  const [showWalkthrough, setShowWalkthrough] = useState(true);
  const [numIcons, setNumIcons] = useState(4);
  const [selectedFilters, setSelectedFilters] = useState<Set<string>>(new Set());
  const [collapseFilters, setCollapseFilters] = useState<Set<string>>(new Set());
  const [visibleItems, setVisibleItems] = useState<ShufflerProduct[]>([]);
  const [product, setProduct] = useState<ShufflerProduct | undefined>(undefined);
  const [relatedProducts, setRelatedProducts] = useState<ShufflerProduct[]>([]);
  const [showRelatedProducts, setShowRelatedProducts] = useState(true);
  const [productOptionGroups, setProductOptionGroups] = useState<OptionGroupAPIData[]>([]);
  const [currentOptionGroup, setCurrentOptionGroup] = useState<OptionGroupAPIData | undefined>(
    undefined
  );
  const [selectedOptions, setSelectedOptions] = useState<Map<string, number>>(new Map());
  const [sortBy, setSortBy] = useState('default');

  const staticFilterData = useMemo(() => {
    if (!activeCatalog?.name) return null;

    return FilterData[activeCatalog.name];
  }, [activeCatalog]);

  const catalogFilters = useMemo(() => {
    return staticFilterData ? staticFilterData.filters : [];
  }, [staticFilterData]);

  const catalogOptionGroups = useMemo(() => {
    return staticFilterData ? staticFilterData.optionGroups : [];
  }, [staticFilterData, optionGroups]);

  const allFilterableProducts = useMemo(() => {
    const formats = staticFilterData?.filters.find((f) => f.groupname === 'format');

    return subcatalogs
      .flatMap((s) => {
        return s.templates.flatMap((pId) => {
          const p = productsById[pId];
          if (!p) return null;

          const sp = new ShufflerProduct(s.name, p);

          // Add the format from the static data, if available
          if (formats) {
            let added = false;
            formats.groupfilters.forEach((filter) => {
              if (!added && filter.products.includes(sp.uid)) {
                sp.format = filter.name;
                added = true;
              }
            });
          }

          return sp;
        });
      })
      .filter(notNull)
      .reverse();
  }, [subcatalogs, productsById, staticFilterData]);

  const metadata = useMemo(() => {
    const ret: { numphotos: number[]; hascustomtext: boolean[] } = {
      numphotos: [],
      hascustomtext: [false],
    };

    const numSet = new Set<number>();
    allFilterableProducts.forEach((p) => {
      if (p.metaData.hascustomtext) {
        ret.hascustomtext = [true];
      }

      numSet.add(p.metaData.numphotos);
    });

    ret.numphotos = Array.from(numSet);

    return ret;
  }, [allFilterableProducts]);

  const reducedFilters = useMemo(() => {
    if (!staticFilterData) return [];

    const { filters, searchFilters } = staticFilterData;

    return filters
      .filter(({ groupname }) => searchFilters.includes(groupname))
      .reduce((a: ShufflerFilterItem[], { groupfilters }) => [...a, ...groupfilters], []);
  }, [staticFilterData]);

  // byFilter is the click handler for each filter link or checkbox
  const byFilter = useCallback(
    (target: EventTarget) => {
      if (target instanceof HTMLElement) {
        setStatus('filtering');
        const t = findAncestor(target, '[data-groupname]');
        if (!t) return;

        const filterGroup = t.dataset.groupname;
        const metaFilter = t.dataset.meta;
        const targetValue = t.dataset.value || t.getAttribute('value');
        const filters = new Set([...selectedFilters]);
        const thisFilterName = `${targetValue}:${filterGroup || metaFilter || ''}`;
        const filterIsActive = filters.has(thisFilterName);

        if (!filterGroup) {
          const filterName = metaFilter ? `:${metaFilter}` : thisFilterName;
          const toDelete = [...selectedFilters.values()].find(
            (f) => f.indexOf(filterName) === f.length - 1
          );
          if (toDelete) {
            filters.delete(toDelete);
          }
        }

        if (filterIsActive) {
          filters.delete(thisFilterName);
        } else if (!filterIsActive) {
          filters.add(thisFilterName);
        }
        setSelectedFilters(filters);
      }
    },
    [selectedFilters]
  );

  const filterCollapse = useCallback(
    ({ currentTarget }: React.MouseEvent<SVGSVGElement>) => {
      const v = currentTarget.dataset.groupname?.toLowerCase();
      if (!v) return;

      const reveal = collapseFilters.has(v);
      currentTarget.classList.toggle('fa-chevron-down', reveal);
      currentTarget.classList.toggle('fa-chevron-up', !reveal);
      if (reveal) {
        collapseFilters.delete(v);
      } else {
        collapseFilters.add(v);
      }
      setCollapseFilters(new Set([...collapseFilters]));
    },
    [collapseFilters]
  );

  const defaultSort = useCallback(
    (a: ShufflerProduct, b: ShufflerProduct) => {
      const featured = staticFilterData?.featured;
      if (!featured) return 0;

      const fa = featured.find((fp) => fp === a.uid);
      const fb = featured.find((fp) => fp === b.uid);
      if (fa && !fb) {
        return -1;
      }
      if (fb && !fa) {
        return 1;
      }
      return 0;
    },
    [staticFilterData]
  );

  const photoUpSort = (a: ShufflerProduct, b: ShufflerProduct) => {
    const countA = a.metaData.numphotos;
    const countB = b.metaData.numphotos;
    return countA - countB;
  };

  const photoDownSort = (a: ShufflerProduct, b: ShufflerProduct) => {
    const countA = a.metaData.numphotos;
    const countB = b.metaData.numphotos;
    return countB - countA;
  };

  const sizeUpSort = ({ size: a }: ShufflerProduct, { size: b }: ShufflerProduct) => {
    const aa = a.split('x').reduce((i, v) => {
      // eslint-disable-next-line no-param-reassign
      i += parseFloat(v);
      return i;
    }, 0);
    const bb = b.split('x').reduce((i, v) => {
      // eslint-disable-next-line no-param-reassign
      i += parseFloat(v);
      return i;
    }, 0);
    return aa - bb;
  };

  const sizeDownSort = ({ size: a }: ShufflerProduct, { size: b }: ShufflerProduct) => {
    const aa = a.split('x').reduce((i, v) => {
      // eslint-disable-next-line no-param-reassign
      i += parseFloat(v);
      return i;
    }, 0);
    const bb = b.split('x').reduce((i, v) => {
      // eslint-disable-next-line no-param-reassign
      i += parseFloat(v);
      return i;
    }, 0);
    return bb - aa;
  };

  const itemClick = useCallback(
    ({ currentTarget }: React.MouseEvent<HTMLButtonElement>) => {
      const { id: productId, catalog: catalogName } = currentTarget.dataset;
      if (!productId || !catalogName) return;

      const containingSubcatalog = subcatalogs.find(({ name }) => name === catalogName);
      if (!containingSubcatalog) return;

      dispatch(
        canvases.actions.changeActiveCanvasConfig.multi({
          subcatalog: containingSubcatalog.id,
          product: parseInt(productId),
        })
      );
      setProduct(allFilterableProducts.find(({ id }) => id === parseInt(productId)));
    },
    [dispatch, allFilterableProducts, subcatalogs]
  );

  const getNavigableItems = useCallback(() => {
    if (!shufflerElem.current) return [];

    return Array.from(shufflerElem.current.children)
      .filter((e) => !e.classList.contains('shuffle-sizer'))
      .filter((e) => !e.classList.contains('shuffle-item--hidden')) as HTMLButtonElement[];
  }, [shufflerElem]);

  const navigateItemPrevious = useCallback(
    (el: HTMLButtonElement) => {
      const v = getNavigableItems();
      const i = v.indexOf(el);
      const n = i === 0 ? v.length - 1 : i - 1;
      v[n].focus();
    },
    [getNavigableItems]
  );

  const navigateItemNext = useCallback(
    (el: HTMLButtonElement) => {
      const v = getNavigableItems();
      const i = v.indexOf(el);
      const n = i < v.length - 1 ? i + 1 : 0;
      v[n].focus();
    },
    [getNavigableItems]
  );

  const navigateItemAbove = useCallback(
    (el: HTMLButtonElement) => {
      const v = getNavigableItems().reverse();
      const { x: elemLeft, y: elemTop } = el.getBoundingClientRect();
      const n = v.find((t) => {
        const { x: tLeft, y: tTop } = t.getBoundingClientRect();
        return t !== el && tLeft === elemLeft && tTop < elemTop;
      });
      const e = n || el;
      e.focus();
    },
    [getNavigableItems]
  );

  const navigateItemBelow = useCallback(
    (el: HTMLButtonElement) => {
      const v = getNavigableItems();
      const { x: elemLeft, y: elemTop } = el.getBoundingClientRect();
      const n = v.find((t) => {
        const { x: tLeft, y: tTop } = t.getBoundingClientRect();
        return t !== el && tLeft === elemLeft && tTop > elemTop;
      });
      const e = n || el;
      e.focus();
    },
    [getNavigableItems]
  );

  const handleItemNavigation = useCallback(
    ({ key, currentTarget }: React.KeyboardEvent<HTMLButtonElement>) => {
      switch (key) {
        case 'ArrowRight':
          return navigateItemNext(currentTarget);
        case 'ArrowLeft':
          return navigateItemPrevious(currentTarget);
        case 'ArrowUp':
          return navigateItemAbove(currentTarget);
        case 'ArrowDown':
          return navigateItemBelow(currentTarget);
        default:
          return false;
      }
    },
    [navigateItemAbove, navigateItemBelow, navigateItemNext, navigateItemPrevious]
  );

  const optionGroupClick = useCallback(
    ({ currentTarget }: React.MouseEvent<HTMLDivElement>, forceToggle?: boolean) => {
      const optionGroup = productOptionGroups.find(
        (g) => g.name === currentTarget.dataset.groupname
      );
      if (
        currentOptionGroup === optionGroup &&
        (forceToggle === undefined || forceToggle === false)
      ) {
        setCurrentOptionGroup(undefined);
      } else if (forceToggle === undefined || forceToggle === true) {
        setCurrentOptionGroup(optionGroup);
      }
    },
    [currentOptionGroup, productOptionGroups]
  );

  const optionClick = ({
    currentTarget: {
      dataset: { id: targetId },
    },
  }: React.MouseEvent<HTMLDivElement> | React.KeyboardEvent<HTMLDivElement>) => {
    if (!currentOptionGroup) return;

    const option = currentOptionGroup.options.find((o) => o.id.toString() === targetId?.toString());
    const currentlySelectedOptions = new Map([...selectedOptions.entries()]);

    if (!option) return;

    currentlySelectedOptions.set(currentOptionGroup.name, option.id);

    const optionPair: OptionalPair<number, number> = [currentOptionGroup.id, option.id];
    const payload = { options: [optionPair] };

    dispatch(canvases.actions.changeActiveCanvasConfig.options(payload));
    setSelectedOptions(currentlySelectedOptions);
    // close the option group after selecting option
    setCurrentOptionGroup(undefined);
  };

  const categoryProducts = useMemo(() => {
    if (!staticFilterData) return [];

    const selectedCategories: string[] = [];
    const nonCats: string[] = [];
    const filterGroups: Record<string, string[]> = {};

    const { filters } = staticFilterData;

    // assign the filters to either the categories or non categories
    [...selectedFilters].forEach((filt) => {
      const [, t] = filt.split(':');
      (t === 'category' ? selectedCategories : nonCats).push(filt);
    });

    let selectedFilterProducts =
      selectedFilters.size > 0 && selectedCategories.length > 0 ? [] : [...allFilterableProducts];

    selectedCategories.forEach((filt) => {
      const [filterValue] = filt.split(':');
      selectedFilterProducts = selectedFilterProducts.concat(
        allFilterableProducts.filter(({ subcatalog }) => subcatalog === filterValue)
      );
    });

    nonCats.forEach((filt) => {
      const [filterValue, filterType] = filt.split(':');
      const metaFilter = /^meta--/.test(filterType);
      filterGroups[filterType] = filterGroups[filterType] || [];
      // find the group that matches the filter
      const { groupfilters: groupedFilters } =
        filters.find(({ groupname }) => groupname && groupname === filterType) || {};

      const { products: fProducts } =
        (!!groupedFilters && groupedFilters.find(({ name }) => name === filterValue)) || {};

      if (metaFilter) {
        const [, metaKey] = filterType.split('meta--') as [
          string,
          keyof ShufflerProduct['metaData']
        ];
        const mProducts = allFilterableProducts
          .filter((p) => p.metaData[metaKey].toString() === filterValue)
          .map((p) => p.uid);
        if (mProducts && mProducts.length) {
          filterGroups[filterType].push(...mProducts);
        }
      }
      if (fProducts && fProducts.length) {
        filterGroups[filterType].push(...fProducts);
      }
    });

    const groups = Object.values(filterGroups).sort(({ length: a }, { length: b }) => a - b);

    // product has to be in all groups so check the shortest
    // pull group with the fewest products and shorten `groups` array
    const shortestGroup = groups.shift();
    const productsInAllGroups = shortestGroup
      ? shortestGroup.filter((p) => {
          let res = true;
          if (groups.length > 0) {
            groups.forEach((group) => {
              res = res && group.includes(p);
            });
          }
          return res;
        })
      : [];

    const groupProducts =
      productsInAllGroups.length === 0
        ? []
        : productsInAllGroups.reduce((a: ShufflerProduct[], productUid) => {
            const found = selectedFilterProducts.find(({ uid }) => uid === productUid);
            if (found) {
              a.push(found);
            }
            return a;
          }, []);

    return [
      ...new Set(
        (nonCats.length === 0 ? [...selectedFilterProducts] : groupProducts).filter(notNull)
      ),
    ];
  }, [selectedFilters, allFilterableProducts, staticFilterData]);

  const filterSearch = useCallback(
    (target: HTMLInputElement) => {
      if (!staticFilterData) return;

      const searchterms = target.value.toLowerCase();
      const { searchParams } = staticFilterData;

      // if there are search terms in the search field,
      // search for products matching those terms.
      const productsMatchingSearchTerms =
        searchterms === ''
          ? categoryProducts
          : categoryProducts.filter((p) => {
              let ret = p.subcatalog.toLowerCase().includes(searchterms);
              if (!ret) {
                // searchParams are the specified fields to match against
                searchParams.forEach((s) => {
                  ret =
                    ret ||
                    (propInShufflerProduct(s) &&
                      !!p[s]?.toString().toLowerCase().includes(searchterms));
                });
              }
              return ret;
            });

      // if there are no search terms use the categoryProducts
      // if there are search terms
      const searchableFilterProducts = searchterms
        ? [
            ...reducedFilters
              .filter(({ name }) => name.toLowerCase().indexOf(searchterms) > -1)
              .reduce((a, { products }) => {
                products.forEach(a.add, a);
                return a;
              }, new Set()),
          ]
            .map((matchUid) =>
              categoryProducts.find(({ uid: productUid }) => productUid === matchUid)
            )
            .filter(notNull)
        : categoryProducts;

      const sortFunction = {
        default: defaultSort,
        photo_up: photoUpSort,
        photo_down: photoDownSort,
        size_up: sizeUpSort,
        size_down: sizeDownSort,
      }[sortBy];

      setVisibleItems(
        [...new Set(productsMatchingSearchTerms.concat(searchableFilterProducts))].sort(
          sortFunction
        )
      );
    },
    [categoryProducts, reducedFilters, defaultSort, staticFilterData, sortBy]
  );

  const showProductsInView = useCallback(() => {
    const { current } = elem;
    if (!current) return;

    const buttons = Array.from(current.querySelectorAll('button.catalog-item'));
    if (buttons.length > 0) {
      buttons.forEach((tile) => {
        const { top } = tile.getBoundingClientRect();
        const container = tile.querySelector('.item_container');
        if (container && top < window.innerHeight + 500) {
          tile.classList.remove('invisible');
          container.classList.add('animate__animated');
        }
      });
      setStatus('idle');
    } else if (init) {
      setStatus('idle');
    }
  }, [elem, init]);

  const tileKey = useMemo(() => `${[...selectedFilters].toString()}`, [selectedFilters]);

  useEffect(() => {
    if (!showWalkthrough) {
      setInit(true);
      showProductsInView();
    }
  }, [showWalkthrough, showProductsInView]);

  useEffect(() => {
    const { current } = elem;
    if (current) {
      const scrollListener = debounce(() => showProductsInView(), 100, { leading: true });
      const shufflerElement = shufflerElem.current;
      shufflerElement?.addEventListener('scroll', scrollListener);

      return () => {
        shufflerElement?.removeEventListener('scroll', scrollListener);
      };
    }
  }, [showProductsInView]);

  useEffect(() => {
    if (status === 'idle' && !init) {
      setInit(true);
    }
  }, [status, init]);

  useEffect(() => {
    setVisibleItems(allFilterableProducts);
  }, [allFilterableProducts]);

  useEffect(() => {
    showProductsInView();
  }, [visibleItems, showProductsInView]);

  useEffect(() => {
    if (allFilterableProducts.length) {
      // check to url for a flag to automatically launch product detail
      if (paramFilterDetail && activeProduct) {
        setProduct(allFilterableProducts.find((p) => p.id === activeProduct.id));
        setShowWalkthrough(false);
      }
    }
  }, [allFilterableProducts, activeProduct]);

  useEffect(() => {
    const input = document.querySelector<HTMLInputElement>('#filter-search-input');

    if (input) {
      filterSearch(input);
    }
  }, [filterSearch, selectedFilters]);

  useEffect(() => {
    if (product && product.relatedProducts) {
      setRelatedProducts(
        allFilterableProducts.filter((p) => product.relatedProducts?.includes(p.uid))
      );
    } else {
      setSelectedOptions(new Map());
    }
  }, [product, allFilterableProducts]);

  useEffect(() => {
    setProductOptionGroups(
      optionGroups.filter(({ name }) => catalogOptionGroups.indexOf(name) > -1)
    );
  }, [optionGroups, catalogOptionGroups]);

  useEffect(() => {
    const { length } = Object.values(productsById);
    if (length === 0) {
      setStatus('pending');
    } else if (length > 0) {
      setStatus('formatting');
    }
  }, [productsById]);

  useEffect(() => {
    if (!showAdvancedFilters) {
      setSelectedOptions(new Map());
    }
  }, [showAdvancedFilters]);

  useEffect(() => {
    showProductsInView();
  }, [numIcons, showProductsInView]);

  const handleZoom = (d: number) => {
    setNumIcons(d);
  };

  const toggleFiltersElem = (e: React.MouseEvent) => {
    e.preventDefault();
    const c = filtersElem.current?.classList;
    const m = mobileFooterElem.current?.classList;
    const s = shufflerElem.current?.classList;

    if (s && s.contains('freeze')) {
      s.remove('freeze');
      shufflerElem.current.style.position = 'relative';
    } else if (s) {
      s.add('freeze');
      shufflerElem.current.style.position = 'fixed';
    }

    if (m && m.contains('show')) {
      m.remove('show');
    } else if (m) {
      m.add('show');
    }

    if (c && c.contains('show')) {
      c.remove('show');
    } else if (c) {
      c.add('show');
    }
  };

  const shufflerContext = useMemo(() => {
    return {
      staticFilterData,
      byFilter,
      metalabels,
      catalogOptionGroups,
      activeCatalog,
      activeProduct,
      currentOptionGroup,
      itemClick,
      handleItemNavigation,
      optionGroupClick,
      optionClick,
      product,
      productOptionGroups,
      relatedProducts,
      showRelatedProducts,
      setShowRelatedProducts,
      selectedOptions,
      setCurrentOptionGroup,
      setProduct,
      FilterData,
      handleZoom,
      filterSearch,
      filterCollapse,
      numIcons,
      selectedFilters,
      setSelectedFilters,
      collapseFilters,
      shufflerElem,
      sortBy,
      setSortBy,
      showWalkthrough,
      setShowWalkthrough,
    };
  }, [
    staticFilterData,
    byFilter,
    catalogOptionGroups,
    collapseFilters,
    activeCatalog,
    currentOptionGroup,
    activeProduct,
    filterCollapse,
    filterSearch,
    handleItemNavigation,
    itemClick,
    numIcons,
    optionGroupClick,
    optionClick,
    product,
    productOptionGroups,
    relatedProducts,
    selectedFilters,
    selectedOptions,
    showRelatedProducts,
    sortBy,
    setSortBy,
    shufflerElem,
    showWalkthrough,
    setShowWalkthrough,
  ]);

  function filterGroupSort(a: ShufflerFilterItem, b: ShufflerFilterItem) {
    if (a.default) {
      if (b.default) {
        return 0;
      }
      return -1;
    }
    if (b.default) {
      if (a.default) {
        return 0;
      }
      return 1;
    }
    return 0;
  }

  const featuredProducts = useMemo(() => {
    return allFilterableProducts.length && staticFilterData?.featured
      ? staticFilterData.featured
          .map((uid) => allFilterableProducts.find((p) => p.uid === uid))
          .filter(notNull)
      : [];
  }, [staticFilterData, allFilterableProducts]);

  function handleShowAdvancedFilters(show: boolean) {
    dispatch(ui.actions.setShowAdvancedFilters(show));
  }

  return (
    <div className="shuffl">
      <div
        ref={elem}
        className={`shuffleContainer ${
          !showAdvancedFilters || catalogFilters.length === 0 ? 'hidden' : ''
        }`}
      >
        <ShufflerContext.Provider value={shufflerContext}>
          {hasMobileNavigation ? (
            <MobileControls
              toggleFilters={toggleFiltersElem}
              handleShowAdvancedFilters={handleShowAdvancedFilters}
            />
          ) : (
            <StandardControls handleShowAdvancedFilters={handleShowAdvancedFilters} />
          )}
          <div className="shuffler_body">
            <div className="filters styles" ref={filtersElem}>
              <div>
                {catalogFilters.map((filtergroup) => (
                  <ShufflerFilterGroup
                    key={filtergroup.groupname}
                    group={filtergroup.groupname}
                    label={filtergroup.groupLabel}
                    filters={filtergroup.groupfilters.sort(filterGroupSort)}
                  />
                ))}

                {metadata &&
                  Object.entries(metadata).map(([key, val]) => (
                    <ShufflerFilterGroup
                      key={key}
                      group={`meta--${key}`}
                      filters={val}
                      type="meta"
                      metakey={key}
                    />
                  ))}
              </div>
            </div>
            <div className="mobile-filter-footer" ref={mobileFooterElem}>
              <span>
                {[...selectedFilters].length > 0 && (
                  <Button
                    type="bare"
                    onClick={() => {
                      setSelectedFilters(new Set());
                    }}
                  >
                    Clear Filters
                  </Button>
                )}
              </span>
              <span>
                <Button type="bare" onClick={toggleFiltersElem}>
                  Show {visibleItems.length} results
                </Button>
              </span>
            </div>
            {!paramFilterDetail && showWalkthrough && (
              <ShufflerWalkthrough
                filters={staticFilterData?.filters || []}
                items={visibleItems}
                featured={featuredProducts}
                handleShowAdvancedFilters={handleShowAdvancedFilters}
                setProduct={setProduct}
                selectedFilters={selectedFilters}
                setSelectedFilters={setSelectedFilters}
                setShowWalkthrough={setShowWalkthrough}
              />
            )}
            <div className="shuffler row shuffle" style={{ margin: '0 auto' }} ref={shufflerElem}>
              {!showWalkthrough &&
                visibleItems.map((p) => {
                  return (
                    <ShufflerProductTile
                      key={`${p.uid}_${tileKey}`}
                      shufflerProduct={p}
                      numIcons={numIcons}
                    />
                  );
                })}
            </div>
            <div className={`shuffler_status${init ? ' gohome' : ''}`}>
              {status === 'pending' && <div>Loading catalog...</div>}
              {status === 'formatting' && (
                <div>preparing {Object.values(productsById).length} products...</div>
              )}
              {status === 'filtering' && <div>filtering {categoryProducts.length} products...</div>}
              {status === 'idle' && <div>displaying {visibleItems.length} results</div>}
            </div>
          </div>

          {visibleItems.length === 0 && selectedFilters.size > 0 && init && !showWalkthrough && (
            <div className="no-results-message">
              <div>No results found.</div>
              <div>Please try a different search or reduce the number of filters.</div>
            </div>
          )}
          {product && (
            <ShufflerProductDetail
              onClick={optionClick}
              handleShowAdvancedFilters={handleShowAdvancedFilters}
            />
          )}
        </ShufflerContext.Provider>
      </div>
    </div>
  );
}

export default Shuffler;
